/**
 * Move focus to the next control the user can actually fill in.
 *
 * Used by fields that are filled in as a run — the cabinet's height, width and
 * depth, or a cut-list row's two dimensions — where Enter reads as "next" rather
 * than "save the whole thing".
 *
 * "Can be completed" deliberately means inputs, selects and textareas only.
 * Including buttons would land Enter on "Delete row" or "Save", which is the
 * opposite of helpful when running down a column of measurements.
 *
 * Scoped to the surrounding form so focus cannot escape into the page around it.
 * There is no visibility test: this app renders one tab at a time rather than
 * hiding the others with CSS, so anything off-screen is simply not in the DOM —
 * and `offsetParent`, the usual check, is always null under jsdom.
 */
export function focusNextFillableControl(from: HTMLElement): void {
  const scope = from.closest('form') ?? document.body;
  const candidates = Array.from(
    scope.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea',
    ),
  ).filter(
    (el) =>
      !el.disabled &&
      !(el instanceof HTMLInputElement && el.readOnly) &&
      !(el instanceof HTMLInputElement && el.type === 'hidden') &&
      el.tabIndex !== -1,
  );

  const at = candidates.indexOf(from as never);
  const next = at === -1 ? undefined : candidates[at + 1];
  if (!next) return;

  next.focus();
  // Pre-select the value so typing replaces it, which is what you want when
  // re-measuring a run. Only for the types where selecting means anything —
  // `select()` throws on a checkbox in some browsers.
  if (next instanceof HTMLInputElement && SELECTABLE_TYPES.has(next.type)) {
    next.select();
  } else if (next instanceof HTMLTextAreaElement) {
    next.select();
  }
}

const SELECTABLE_TYPES = new Set(['text', 'number', 'search', 'tel', 'url', 'password', 'email']);
